import { XMLBuilder, XMLParser } from 'fast-xml-parser';
import { v1 } from 'uuid';
import { backendServices } from '../../services';
import {
    BusinessDocType,
    FieldDictionaryControlSubType,
    FieldDictionaryControlType,
    Optional,
    RecursivePartial,
} from '../../types';

type BaseItemProps = {
    id: string;
    name: string;
    required: boolean;
};

export function isQuestionnaireItemRequired(item: QuestionnaireItem): boolean {
    if (item.type === 'Label') {
        return false;
    }
    return item.required !== false;
}

/** Checked checkbox or non-empty string. Unchecked / false / blank does not count as filling the form. */
export function questionnaireAnswerIsFilled(value: unknown): boolean {
    if (value === true) {
        return true;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
        return true;
    }
    return false;
}

/** Matches Supplier Portal save checks: only undefined / null / '' are missing. Boolean false is a stored answer. */
export function questionnaireAnswerIsMissing(value: unknown): boolean {
    return value === undefined || value === null || value === '';
}

export function questionnaireHasFilledAnswers(
    items: QuestionnaireItem[],
    answers: Record<string, unknown> | null | undefined
): boolean {
    return items.some((item, itemIndex) => {
        if (item.type === 'Label') {
            return false;
        }
        return questionnaireAnswerIsFilled(answers?.[`Item${itemIndex + 1}`]);
    });
}

type TextFieldItem = BaseItemProps & {
    type: 'TextField';
};

type MultiLineTextFieldItem = BaseItemProps & {
    type: 'MultiLineTextField';
};

type CheckboxItem = BaseItemProps & {
    type: 'Checkbox';
    leftLabel: string;
};

type DropdownItem = BaseItemProps & {
    type: 'Dropdown';
    options: string;
};

type YesNoDropdownItem = BaseItemProps & {
    type: 'YesNoDropdown';
};

type LabelItem = BaseItemProps & {
    type: 'Label';
    // customCSSClassName: string;
};

type RadioButtonItem = BaseItemProps & {
    type: 'RadioButton';
    options: string;
};

export type QuestionnaireItem =
    | TextFieldItem
    | MultiLineTextFieldItem
    | CheckboxItem
    | LabelItem
    | RadioButtonItem
    | DropdownItem
    | YesNoDropdownItem;

export type QuestionnaireFormData = {
    name: string;
    description: string;
    items: QuestionnaireItem[];
    businessDocTypeViewModel: Optional<
        backendServices.ViewModels.BusinessDocTypeViewModel,
        | 'BusinessDocType_US'
        | 'LastUpdateDate'
        | 'Rowversion'
        | 'DocumentClasses' // not relevant for creation
        | 'BusinessDocSubTypeId' // not relevant for creation
    >;
    fieldDictionaryLayoutViewModel: RecursivePartial<backendServices.ViewModels.DocumentLayoutViewModel>;
};

function questionnaireItemsToXMLSkeleton(items: QuestionnaireItem[]): string {
    const builder = new XMLBuilder({});

    const jsonSubtree = items.reduce((subtree, item, index) => {
        const itemIndex = index + 1;

        subtree[`Item${itemIndex}`] = '';

        return subtree;
    }, {} as Record<`${'Item'}${number}`, string>);

    const xmlSubtree = builder.build(jsonSubtree);

    return `<BusinessDocument><IdentifyingNumber/><ReferenceDocumentIdentifyingNumber/><DocumentDate/><CustomerName/><CustomerNumber/><VendorName/><VendorNumber/><SIMRequestId/><SIMDocument>${xmlSubtree}</SIMDocument></BusinessDocument>`;
}

function createFieldDictionaryFieldsViewModel(
    items: QuestionnaireItem[]
): RecursivePartial<backendServices.ViewModels.FieldDictionaryViewModel>[] {
    return items.map((item, idx) => {
        const fieldName = `Item${idx + 1}`;

        const defaultConfig = {
            RecordType: 1,
            RecordType_US: 'Master' as const,
            BusinessDocType: BusinessDocType.SIMDocument,
            BuyerCompanyId: null,
            XPath: fieldName,
            JSONPath: fieldName,
            EntityName: 1,
            FieldName: fieldName,
            Label: item.name,
            Visible: true,
            ShowVisibilityToggleCheckbox: false,
            Required: isQuestionnaireItemRequired(item),
            ControlType: FieldDictionaryControlType.SingleLine,
            ControlFormat: {
                maxchar: '50',
                view_label_bold: 'true',
            },
            LabelCssClass: null,
            ValueCssClass: null,
            Culture: 'en-US',
            Editability: 1,
            SIMDocumentClass: 0, // needs to be overridden with the value of the BusinessDocSubType skeleton being created
        };

        switch (item.type) {
            case 'MultiLineTextField': {
                return {
                    ...defaultConfig,
                    ControlType: FieldDictionaryControlType.MultipleLine,
                };
            }
            case 'Checkbox': {
                const hideLeftLabel = !item.leftLabel.length ? 'true' : 'false';

                return {
                    ...defaultConfig,
                    ControlType: FieldDictionaryControlType.Checkbox,
                    ControlFormat: {
                        leftLabelText: item.leftLabel,
                        hideLeftLabel,
                    },
                };
            }
            case 'Dropdown': {
                return {
                    ...defaultConfig,
                    ControlType: FieldDictionaryControlType.CustomDropdown,
                    ControlFormat: {
                        options: item.options,
                    },
                };
            }
            case 'YesNoDropdown': {
                return {
                    ...defaultConfig,
                    ControlType: FieldDictionaryControlType.CustomDropdown,
                    ControlFormat: {
                        options: 'true_Yes|false_No',
                    },
                };
            }
            case 'Label': {
                return {
                    ...defaultConfig,
                    ControlType: FieldDictionaryControlType.Label,
                };
            }
            case 'RadioButton': {
                return {
                    ...defaultConfig,
                    ControlType: FieldDictionaryControlType.RadioButton,
                    ControlSubType: FieldDictionaryControlSubType.Generic,
                    ControlFormat: {
                        options: item.options,
                    },
                };
            }
            case 'TextField':
            default:
                return defaultConfig;
        }
    });
}

export function businessDocTypeFormToViewModels({
    businessDocTypeViewModel,
    description,
    name,
    items: questionnaireItems,
}: QuestionnaireFormData): [
    RecursivePartial<backendServices.ViewModels.BusinessDocTypeViewModel>,
    RecursivePartial<backendServices.ViewModels.FieldDictionaryViewModel>[]
] {
    return [
        {
            ...businessDocTypeViewModel,
            Name: name,
            Description: description,
            Skeleton: questionnaireItemsToXMLSkeleton(questionnaireItems),
        },
        createFieldDictionaryFieldsViewModel(questionnaireItems),
    ];
}

export const stringifyItemOptions = (options: { label: string; value: string }[]) => {
    return options.map((option) => `${option.value}_${option.label}`).join('|');
};

export const parseItemOptions = (options: string) => {
    return options
        .split('|')
        .filter(Boolean)
        .map((option) => ({ label: option.split('_')[1], value: option.split('_')[0] }));
};

function getItemConfig(
    fieldName: string,
    fieldDictionaryLayout: backendServices.ViewModels.DocumentLayoutViewModel['Layouts'][number]
): QuestionnaireItem {
    const field = fieldDictionaryLayout.Cells[0].Fields.find(
        (fieldDefinition) => fieldDefinition.FieldName === fieldName
    );

    if (!field) {
        throw new Error(`Field ${fieldName} not found in field dictionary layout`);
    }

    const name = field.Label;
    const fieldRequired = field.Required === true;

    switch (field.ControlType) {
        case FieldDictionaryControlType.SingleLine:
            return {
                id: v1(),
                name,
                type: 'TextField',
                required: fieldRequired,
            };
        case FieldDictionaryControlType.MultipleLine:
            return {
                id: v1(),
                name,
                type: 'MultiLineTextField',
                required: fieldRequired,
            };
        case FieldDictionaryControlType.Checkbox: {
            const leftLabel = field.ControlFormat?.leftLabelText ?? '';

            return {
                id: v1(),
                name,
                type: 'Checkbox',
                leftLabel,
                required: fieldRequired,
            };
        }
        case FieldDictionaryControlType.CustomDropdown: {
            const options = parseItemOptions(field.ControlFormat?.options ?? '');

            const isYesNoDropdown = options.length === 2 && options[0].label === 'Yes' && options[1].label === 'No';

            return {
                id: v1(),
                name,
                type: isYesNoDropdown ? 'YesNoDropdown' : 'Dropdown',
                options: stringifyItemOptions(options),
                required: fieldRequired,
            };
        }
        case FieldDictionaryControlType.Label: {
            return {
                id: v1(),
                name,
                type: 'Label',
                required: false,
            };
        }
        case FieldDictionaryControlType.RadioButton: {
            const options = parseItemOptions(field.ControlFormat?.options ?? '');

            return {
                id: v1(),
                name,
                type: 'RadioButton',
                options: stringifyItemOptions(options),
                required: fieldRequired,
            };
        }
        default:
            return {
                id: v1(),
                name,
                type: 'TextField',
                required: fieldRequired,
            };
    }
}

export function extractQuestionnaireItems(
    skeleton: string | null,
    fieldDictionaryLayoutViewModel: backendServices.ViewModels.DocumentLayoutViewModel
): QuestionnaireItem[] {
    if (!skeleton) {
        return [];
    }

    const fieldDictionaryLayout = fieldDictionaryLayoutViewModel.Layouts?.[0];

    // No field-dictionary layout configured for this questionnaire; skip its
    // items instead of crashing the whole post-signup flow.
    if (!fieldDictionaryLayout?.Cells?.length) {
        return [];
    }

    const builder = new XMLParser();
    const json = builder.parse(skeleton);

    const simDocument = json.BusinessDocument.SIMDocument;

    const questionKeys = Object.keys(simDocument).filter((key) => key.startsWith('Item'));

    const items = questionKeys.map((itemKey) => {
        return getItemConfig(itemKey, fieldDictionaryLayout);
    });

    return items;
}

export function viewModelToBusinessDocTypeForm(
    businessDocTypeViewModel: backendServices.ViewModels.BusinessDocTypeViewModel,
    fieldDictionaryLayoutViewModel: backendServices.ViewModels.DocumentLayoutViewModel
): QuestionnaireFormData {
    const extractedItems = extractQuestionnaireItems(businessDocTypeViewModel.Skeleton, fieldDictionaryLayoutViewModel);

    return {
        name: businessDocTypeViewModel.Name,
        description: businessDocTypeViewModel.Description,
        items: extractedItems,
        businessDocTypeViewModel,
        fieldDictionaryLayoutViewModel,
    };
}
